import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES, type Language } from '@lexicon/core';
import { Sheet } from './Sheet';
import { ChevronRightIcon } from './icons';
import { useApp } from '../state/AppContext';

export interface MenuSheetProps {
  open: boolean;
  onClose: () => void;
  onTodaysPuzzle: () => void;
  onJourney: () => void;
  onNewChallenge: () => void;
  onMyChallenges: () => void;
  onHowToPlay: () => void;
  onAbout: () => void;
}

const ITEM_CLASS = 'flex min-h-[52px] w-full items-center justify-between rounded-xl px-3 text-sm';

/**
 * The language switch: both languages side by side, each written in its
 * own language, so the way out of a language you cannot read is always
 * legible. A segmented control rather than a nested sheet — with two
 * options, a sheet to choose between them is a step for its own sake.
 */
function LanguagePicker() {
  const { language, setLanguage, t } = useApp();
  return (
    <div className="flex flex-col gap-1.5 px-3 pt-2">
      <span className="text-xs tracking-[0.14em] text-dim">{t.menu.language.toUpperCase()}</span>
      <div role="group" aria-label={t.menu.language} className="flex gap-2">
        {SUPPORTED_LANGUAGES.map((code: Language) => {
          const selected = code === language;
          return (
            <button
              key={code}
              type="button"
              lang={code}
              aria-pressed={selected}
              onClick={() => { void setLanguage(code); }}
              className={[
                'min-h-[44px] flex-1 rounded-xl border px-3 text-sm font-semibold',
                selected ? 'border-transparent bg-grad-accent text-white' : 'border-hairline text-dim',
              ].join(' ')}
            >
              {LANGUAGE_NAMES[code]}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-dim">{t.menu.languageHint}</p>
    </div>
  );
}

export function MenuSheet({
  open, onClose, onTodaysPuzzle, onJourney, onNewChallenge, onMyChallenges, onHowToPlay, onAbout,
}: MenuSheetProps) {
  const { t } = useApp();
  return (
    <Sheet open={open} title={t.menu.title} onClose={onClose}>
      <ul className="flex flex-col gap-1 pb-4">
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onTodaysPuzzle}>
            {t.menu.todaysPuzzle}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onJourney}>
            {t.menu.journey}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onNewChallenge}>
            {t.menu.newChallenge}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onMyChallenges}>
            {t.menu.myChallenges}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onHowToPlay}>
            {t.menu.howToPlay}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
        <li>
          <button type="button" className={ITEM_CLASS} onClick={onAbout}>
            {t.menu.about}
            <ChevronRightIcon className="h-4 w-4 text-dim" />
          </button>
        </li>
      </ul>
      <div className="border-t border-hairline pb-4">
        <LanguagePicker />
      </div>
    </Sheet>
  );
}
